import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, type SxProps, type Theme } from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { LoadingIndicator } from '@dhl/components'

import { useSearch } from '~/search/model/search.js'

import type { BaseSearchResultProps } from './SearchResultListItem.js'
import { SearchResultList } from './SearchResultList.js'
import { PieceSearchResult } from './PieceSearchResult.js'
import { AddressSearchResult } from './AddressSearchResult.js'
import { AddressBookSearchResult } from './AddressBookSearchResult.js'
import { NoSearchResults } from './NoSearchResults.js'
import { useSearchStore } from '~/search/model/useSearchStore.js'

type Props = {
  sx?: SxProps<Theme>
}

type SearchResultDisplayConfig = {
  title: string
  component: React.ComponentType<BaseSearchResultProps>
  results: unknown[]
}

export const SearchResults: FC<Props> = ({ sx }) => {
  const { t } = useTranslation()
  const { searchTerm } = useSearchStore()

  const {
    data: {
      inTransitIncomingPieces,
      deliveredIncomingPieces,
      returnedIncomingPieces,
      inTransitOutgoingPieces,
      deliveredOutgoingPieces,
      profileAddresses,
      addressBookEntries,
      resultsFound,
    },
    isLoading,
  } = useSearch(searchTerm)

  const resultsConfig: SearchResultDisplayConfig[] = [
    { title: 'portal.common.onItsWayText', component: PieceSearchResult, results: inTransitIncomingPieces },
    { title: 'portal.common.deliveredText', component: PieceSearchResult, results: deliveredIncomingPieces },
    { title: 'portal.common.inTransitOutgoingText', component: PieceSearchResult, results: inTransitOutgoingPieces },
    { title: 'portal.common.deliveredOutgoingText', component: PieceSearchResult, results: deliveredOutgoingPieces },
    { title: 'portal.common.returnedParcelsText', component: PieceSearchResult, results: returnedIncomingPieces },
    { title: 'portal.profile.addressListPage.titleText', component: AddressSearchResult, results: profileAddresses },
    {
      title: 'portal.profile.addressBookPage.titleText',
      component: AddressBookSearchResult,
      results: addressBookEntries,
    },
  ]

  if (!searchTerm) {
    return null
  }

  if (isLoading) {
    return <LoadingIndicator isFullHeight={false} />
  }

  return (
    <Box minHeight="inherit" sx={sx} data-test="search-results-container">
      {!resultsFound && <NoSearchResults />}
      {resultsFound && (
        <>
          {resultsConfig
            .filter(({ results }) => results.length)
            .map(({ component: Component, results, title }) => (
              // @ts-ignore - this is a dynamic key
              <SearchResultList key={title} title={t(title)}>
                {results.map((result) => (
                  <Component data={result} key={uuidv4()} />
                ))}
              </SearchResultList>
            ))}
        </>
      )}
    </Box>
  )
}
